import intl from "react-intl-universal";
import {
  endOfDay,
  formatFilterDate,
  startOfDay,
} from "../../../../Components/Molecules/GridHistory/gridFilterUtils";

export const BAAS_API_LOG_STATUS_CLASSES = ["2xx", "4xx", "5xx", "other"];
export const BAAS_API_LOG_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

/** Portal / internal prefixes — not partner-facing public API. */
const PRIVATE_API_PATH_PREFIXES = ["/v1/instances", "/v1/platform", "/v1/mobile"];

/**
 * Console portal noise (Baas Console in carteira). Used as fallback when
 * telemetry has no `client` tag yet (pre X-Baas-Client).
 */
const CARTEIRA_CONSOLE_PATH_PREFIXES = ["/v1/instances", "/v1/platform"];

/** Values written by MobileService proxies into telemetry metadata.client. */
const CARTEIRA_CLIENT_TAGS = new Set(["console", "carteira"]);

const LOGS_HIDE_PREFS_STORAGE_KEY = "baas.api.logs.hidePrefs.v2";

export const EMPTY_BAAS_API_LOGS_FILTERS = {
  searchQuery: "",
  dateFrom: null,
  dateTo: null,
  statusClasses: [],
  methods: [],
};

export const DEFAULT_BAAS_API_LOGS_HIDE_PREFS = {
  hideCarteira: true,
  hidePrivateRoutes: true,
};

export const getBaasApiLogStatusOptions = () =>
  BAAS_API_LOG_STATUS_CLASSES.map((statusClass) => ({
    id: statusClass,
    label: intl.get(`baas.api.logs.statusClass.${statusClass}`),
  }));

export const getBaasApiLogMethodOptions = () =>
  BAAS_API_LOG_METHODS.map((method) => ({
    id: method,
    label: method,
  }));

const normalizeLogPath = (path) => {
  const raw = String(path || "").trim().toLowerCase();
  if (!raw) return "";
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return withSlash.replace(/\/+$/, "") || "/";
};

const pathMatchesPrefix = (path, prefix) => {
  const normalized = normalizeLogPath(path);
  const normalizedPrefix = normalizeLogPath(prefix);
  return (
    normalized === normalizedPrefix || normalized.startsWith(`${normalizedPrefix}/`)
  );
};

export const isPrivateApiLogPath = (path) =>
  PRIVATE_API_PATH_PREFIXES.some((prefix) => pathMatchesPrefix(path, prefix));

/** Console catalog / infra routes that are never partner product APIs. */
export const isConsoleInfrastructurePath = (path) => {
  const normalized = normalizeLogPath(path);
  if (
    normalized.includes("/internal-transfer-options")
    || normalized.includes("/peer-roots")
    || normalized.includes("/peer-statements")
    || normalized.includes("/peer-balances")
    || normalized.includes("/peer-pix-metrics")
    || normalized.includes("/peer-owners")
  ) {
    return true;
  }
  return /^\/v1\/instances\/\d+(\/(features|scopes))?$/.test(normalized);
};

export const isPartnerFacingTopEndpoint = (path) => !isConsoleInfrastructurePath(path);

/**
 * Carteira-originated traffic: tagged by MobileService proxies
 * (`console` = Baas Console, `carteira` = IB wallet account proxy).
 * Untagged historical rows fall back to console portal path prefixes.
 */
export const isCarteiraOriginatedLog = (entry) => {
  const client = String(entry?.client || "").trim().toLowerCase();
  if (CARTEIRA_CLIENT_TAGS.has(client)) return true;
  if (client) return false;
  return CARTEIRA_CONSOLE_PATH_PREFIXES.some((prefix) =>
    pathMatchesPrefix(entry?.path, prefix),
  );
};

export const loadBaasApiLogsHidePrefs = () => {
  try {
    const raw = localStorage.getItem(LOGS_HIDE_PREFS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_BAAS_API_LOGS_HIDE_PREFS };
    const parsed = JSON.parse(raw);
    return {
      hideCarteira: parsed?.hideCarteira !== false,
      hidePrivateRoutes: parsed?.hidePrivateRoutes !== false,
    };
  } catch {
    return { ...DEFAULT_BAAS_API_LOGS_HIDE_PREFS };
  }
};

export const saveBaasApiLogsHidePrefs = (prefs) => {
  localStorage.setItem(
    LOGS_HIDE_PREFS_STORAGE_KEY,
    JSON.stringify({
      hideCarteira: Boolean(prefs?.hideCarteira),
      hidePrivateRoutes: Boolean(prefs?.hidePrivateRoutes),
    }),
  );
};

const getStatusClass = (statusCode) => {
  if (statusCode == null) return "other";
  if (statusCode >= 200 && statusCode < 300) return "2xx";
  if (statusCode >= 400 && statusCode < 500) return "4xx";
  if (statusCode >= 500) return "5xx";
  return "other";
};

const matchesSearch = (entry, query) => {
  if (!query?.trim()) return true;
  const normalized = query.trim().toLowerCase();
  return [entry.path, entry.method, entry.correlationId, entry.statusCode]
    .filter((value) => value != null && value !== "")
    .some((value) => String(value).toLowerCase().includes(normalized));
};

const matchesDateRange = (entry, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  const date = new Date(entry.date);
  if (Number.isNaN(date.getTime())) return false;
  if (dateFrom && date < startOfDay(dateFrom)) return false;
  if (dateTo && date > endOfDay(dateTo)) return false;
  return true;
};

export const filterBaasApiLogs = (
  items = [],
  filters = EMPTY_BAAS_API_LOGS_FILTERS,
  hidePrefs = DEFAULT_BAAS_API_LOGS_HIDE_PREFS,
) =>
  items.filter((entry) => {
    if (hidePrefs?.hideCarteira && isCarteiraOriginatedLog(entry)) return false;
    if (hidePrefs?.hidePrivateRoutes && isPrivateApiLogPath(entry?.path)) return false;
    if (!matchesSearch(entry, filters.searchQuery)) return false;
    if (!matchesDateRange(entry, filters.dateFrom, filters.dateTo)) return false;
    if (
      filters.statusClasses?.length &&
      !filters.statusClasses.includes(getStatusClass(entry.statusCode))
    ) {
      return false;
    }
    if (
      filters.methods?.length &&
      !filters.methods.includes(String(entry.method || "").toUpperCase())
    ) {
      return false;
    }
    return true;
  });

export const hasActiveBaasApiLogsFilters = (filters = EMPTY_BAAS_API_LOGS_FILTERS) =>
  Boolean(
    filters.searchQuery?.trim() ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.statusClasses?.length ||
      filters.methods?.length,
  );

export const isBaasApiLogsChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "status":
      return Boolean(filters.statusClasses?.length);
    case "method":
      return Boolean(filters.methods?.length);
    default:
      return false;
  }
};

export const buildBaasApiLogsFilterTags = (filters = EMPTY_BAAS_API_LOGS_FILTERS) => {
  const tags = [];
  const statusOptions = getBaasApiLogStatusOptions();
  const methodOptions = getBaasApiLogMethodOptions();

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    tags.push({
      key: "date",
      iconType: "date",
      value: `${formatFilterDate(filters.dateFrom)} – ${formatFilterDate(filters.dateTo)}`,
    });
  }

  filters.statusClasses?.forEach((statusClass) => {
    const option = statusOptions.find((item) => item.id === statusClass);
    tags.push({
      key: `statusClasses:${statusClass}`,
      iconType: "status",
      value: option?.label || statusClass,
    });
  });

  filters.methods?.forEach((method) => {
    const option = methodOptions.find((item) => item.id === method);
    tags.push({
      key: `methods:${method}`,
      iconType: "type",
      value: option?.label || method,
    });
  });

  return tags;
};

export const removeBaasApiLogsFilterTag = (filters, tagKey) => {
  if (tagKey === "searchQuery") {
    return { ...filters, searchQuery: "" };
  }
  if (tagKey === "date") {
    return { ...filters, dateFrom: null, dateTo: null };
  }
  if (tagKey.startsWith("statusClasses:")) {
    const statusClass = tagKey.slice("statusClasses:".length);
    return {
      ...filters,
      statusClasses: (filters.statusClasses || []).filter((item) => item !== statusClass),
    };
  }
  if (tagKey.startsWith("methods:")) {
    const method = tagKey.slice("methods:".length);
    return {
      ...filters,
      methods: (filters.methods || []).filter((item) => item !== method),
    };
  }
  return filters;
};
